/* eslint react/display-name: 0 */

import Head from 'next/head';
import React from 'react';
import PropTypes from 'prop-types';

export const defaultMetatags = {
  title: 'Scale AI: The Data Platform for AI',
  description:
    'Trusted by world class companies, Scale delivers high quality training data for AI applications such as self-driving cars, mapping, AR/VR, robotics, and more.',
  ogImg: 'https://scale.com/static/images/global/facebook.png',
  twImg: 'https://scale.com/static/images/global/twitter.png',
  canonical: null,
  noFollow: false,
  noIndex: false
};

const SiteHead = props => {
  const title = props.title ? props.title : defaultMetatags.title;
  const description = props.description ? props.description : defaultMetatags.description;
  const { ogImg, twImg, canonical, noFollow, noIndex } = props;

  return (
    <Head>
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Webapp Stuff */}
      <link rel="manifest" href="/static/global/favicon/site.webmanifest" />
      <meta name="msapplication-TileColor" content="#000000" />
      <meta name="theme-color" content="#000000" />

      {/* Favicon Stuff */}
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/static/global/favicon/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/static/global/favicon/favicon-16x16.png"
      />
      <link rel="shortcut icon" href="/static/global/favicon/favicon.ico" />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/static/global/favicon/apple-touch-icon.png"
      />

      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="format-detection" content="telephone=yes" />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Facebook Sharing Cards */}
      {ogImg && (
        <>
          <meta property="og:image" name="og:image" content={ogImg} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={canonical} />
          <meta property="og:site_name" content="ScaleAI" />
          <meta property="og:type" content="website" />
          <meta property="og:image:type" content="image/png" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </>
      )}

      {/* Twitter Sharing Cards */}
      {twImg && (
        <>
          <meta name="twitter:site" content="@scaleapi" />
          <meta name="twitter:creator" content="@scaleapi" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={twImg} />
          <meta name="twitter:card" content="summary_large_image" />
        </>
      )}

      {/* Robot Crawling */}
      {noFollow && <meta name="robots" content="none" />}
      {noIndex && <meta name="robots" content="noindex,follow" />}
    </Head>
  );
};

SiteHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  canonical: PropTypes.string, // Canonical URL
  ogImg: PropTypes.string, // Facebook sharing card src
  twImg: PropTypes.string, // Twitter sharing card src
  noFollow: PropTypes.bool, // To disallow robot crawling
  noIndex: PropTypes.bool // To disallow index search
};

SiteHead.defaultProps = {
  title: defaultMetatags.title,
  description: defaultMetatags.description,
  ogImg: defaultMetatags.ogImg,
  twImg: defaultMetatags.twImg,
  canonical: defaultMetatags.canonical,
  noFollow: defaultMetatags.noFollow,
  noIndex: defaultMetatags.noIndex
};

export default SiteHead;
